import type { VideoJsPlayer } from 'video.js';

/**
 * Unified interface for all video players.
 */
export interface IMediaPlayerAdapter {
	// Event subscription methods (VideoJS-style)
	on(event: string, handler: () => void): void;
	one(event: string, handler: () => void): void;
	off(event: string, handler: () => void): void;

	// Player state methods
	currentTime(): number | undefined;
	duration(): number | undefined;
	currentSrc(): string | undefined;
	paused(): boolean;

	// Playback control methods
	play(): Promise<void> | void;
	pause(): void;
	muted(value?: boolean): boolean | void;
	el(): HTMLElement | null;
}

/**
 * Adapter for VideoJS Player
 */
export class VideoJsPlayerAdapter implements IMediaPlayerAdapter {
	private player: VideoJsPlayer;

	constructor(player: VideoJsPlayer) {
		this.player = player;
	}

	on(event: string, handler: () => void): void {
		this.player.on(event, handler);
	}

	one(event: string, handler: () => void): void {
		this.player.one(event, handler);
	}

	off(event: string, handler: () => void): void {
		this.player.off(event, handler);
	}

	currentTime(): number | undefined {
		return this.player.currentTime();
	}

	duration(): number | undefined {
		return this.player.duration();
	}

	currentSrc(): string | undefined {
		return this.player.currentSrc();
	}

	paused(): boolean {
		return this.player.paused();
	}

	play(): Promise<void> | void {
		return this.player.play();
	}

	pause(): void {
		this.player.pause();
	}

	muted(value?: boolean): boolean | void {
		if (value !== undefined) {
			this.player.muted(value);
			return undefined;
		}
		return this.player.muted();
	}

	el(): HTMLElement | null {
		return this.player.el() as HTMLElement | null;
	}
}

/**
 * Adapter for Native HTML Video Player
 */
export class NativeVideoPlayerAdapter implements IMediaPlayerAdapter {
	// User-provided event handlers
	private eventHandlers = new Map<string, Set<() => void>>();

	// Internal event listeners, single event listener for each event on the video element
	private eventListeners = new Map<string, () => void>();

	private videoElement: HTMLVideoElement;

	constructor(videoElement: HTMLVideoElement) {
		this.videoElement = videoElement;
	}

	on(event: string, handler: () => void): void {
		if (!this.eventHandlers.has(event)) {
			this.eventHandlers.set(event, new Set());
			const listener = this.createEventListener(event);
			this.eventListeners.set(event, listener);
			this.videoElement.addEventListener(event, listener);
		}
		this.eventHandlers.get(event)!.add(handler);
	}

	one(event: string, handler: () => void): void {
		const onceHandler = () => {
			handler();
			this.off(event, onceHandler);
		};
		this.on(event, onceHandler);
	}

	off(event: string, handler: () => void): void {
		const handlers = this.eventHandlers.get(event);
		if (handlers) {
			handlers.delete(handler);
			if (handlers.size === 0) {
				const listener = this.eventListeners.get(event);
				if (listener) {
					this.videoElement.removeEventListener(event, listener);
					this.eventListeners.delete(event);
				}
				this.eventHandlers.delete(event);
			}
		}
	}

	private createEventListener(event: string): () => void {
		return () => {
			const handlers = this.eventHandlers.get(event);
			if (handlers) {
				// Create a copy of handlers to avoid issues if handlers are modified during iteration
				Array.from(handlers).forEach(handler => {
					try {
						handler();
					} catch (error) {
						console.error(`Error in video event handler for ${event}:`, error);
					}
				});
			}
		};
	}

	currentTime(): number | undefined {
		return this.videoElement.currentTime;
	}

	duration(): number | undefined {
		return this.videoElement.duration || undefined;
	}

	currentSrc(): string | undefined {
		return this.videoElement.currentSrc || undefined;
	}

	paused(): boolean {
		return this.videoElement.paused;
	}

	play(): Promise<void> | void {
		return this.videoElement.play();
	}

	pause(): void {
		this.videoElement.pause();
	}

	muted(value?: boolean): boolean | void {
		if (value !== undefined) {
			this.videoElement.muted = value;
			return undefined;
		}
		return this.videoElement.muted;
	}

	el(): HTMLElement | null {
		return this.videoElement;
	}
}
